import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { ChangePriorityDialog, ConsignmentStatusDialog, useConsignmentActions } from '@/features/consignment-actions';
import { ShowDispositionDialog } from '@/features/show-disposition';

import type { ConsignmentTableData } from '@/entities/consignments';
import { DataTable, type DataTableProps } from '@/shared/ui/data-table';

import { buildConsignmentRowActions } from '../lib/build-row-actions';

interface ConsignmentsDataTableProps extends Omit<DataTableProps<ConsignmentTableData>, 'actions' | 'onRowAction'> {
  onClearSelection?: () => void;
}

export function ConsignmentsDataTable({
  selectedIds = [],
  onSelectionChange,
  onClearSelection,
  onExport,
  ...dataTableProps
}: ConsignmentsDataTableProps) {
  const { t } = useTranslation();
  const { actions, dialogs, isLoading } = useConsignmentActions({
    selectedIds,
    onClearSelection
  });

  const rowActions = useMemo(() => buildConsignmentRowActions({ actions, t }), [actions, t]);

  return (
    <>
      <DataTable
        {...dataTableProps}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        actions={rowActions}
        onExport={onExport}
      />

      <ChangePriorityDialog
        open={dialogs.changePriority.open}
        onOpenChange={dialogs.changePriority.onOpenChange}
        onConfirm={(priority, comments) => dialogs.changePriority.onConfirm(priority, comments)}
      />

      {dialogs.statusChange.target && (
        <ConsignmentStatusDialog
          open={dialogs.statusChange.open}
          onOpenChange={dialogs.statusChange.onOpenChange}
          target={dialogs.statusChange.target}
          isSubmitting={isLoading}
          onConfirm={dialogs.statusChange.onConfirm}
        />
      )}

      <ShowDispositionDialog
        open={dialogs.showDisposition.open}
        onOpenChange={dialogs.showDisposition.onOpenChange}
        consignmentId={dialogs.showDisposition.consignmentId}
      />
    </>
  );
}
